import axios from "axios";
import { Sparkles, CheckCircle2 } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];
export const STATUS_COLOR = { pending: "text-[#D4AF37]", paid: "text-emerald-400", shipped: "text-sky-400", delivered: "text-green-400", cancelled: "text-red-400" };

// Ambassador benefits summary shown in the admin dashboard (pending + approved) — collapsed by default.
export const AmbBenefits = ({ discount, commission, code }) => {
  const items = [
    `${discount}% discount for their audience`,
    `${commission}% commission on every referred order`,
    code ? `Unique referral code — ${code}` : "Unique personal referral code (issued on approval)",
    "Shareable referral link that auto-applies their discount",
    "Downloadable promo graphics — post, story & banner",
    "Live earnings dashboard with payout tracking",
  ];
  return (
    <details className="group w-full border border-[#D4AF37]/20 bg-[#D4AF37]/[0.04] mt-4" data-testid={`amb-benefits-${code || "pending"}`}>
      <summary className="flex items-center gap-2 p-3.5 cursor-pointer list-none select-none">
        <span className="font-mono text-xs text-[#D4AF37] group-open:hidden">[ + ]</span>
        <span className="font-mono text-xs text-[#D4AF37] hidden group-open:inline">[ − ]</span>
        <Sparkles size={12} className="text-[#D4AF37]" />
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]">View Program Benefits &amp; Guidelines</p>
      </summary>
      <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-1.5 px-4 pb-4">
        {items.map((b) => (
          <li key={b} className="flex items-start gap-2 text-xs text-[#A1A1AA]">
            <CheckCircle2 size={13} className="text-[#D4AF37] mt-0.5 shrink-0" /> <span>{b}</span>
          </li>
        ))}
      </ul>
    </details>
  );
};

// Compute inclusive YYYY-MM-DD {start,end} for a sales-export preset.
export const iso = (d) => d.toISOString().slice(0, 10);
export const salesRangeDates = (preset) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case "this-month":
      return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) };
    case "last-month":
      return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) };
    case "this-quarter": {
      const qs = Math.floor(m / 3) * 3;
      return { start: iso(new Date(y, qs, 1)), end: iso(new Date(y, qs + 3, 0)) };
    }
    case "last-quarter": {
      const qs = Math.floor(m / 3) * 3 - 3;
      return { start: iso(new Date(y, qs, 1)), end: iso(new Date(y, qs + 3, 0)) };
    }
    case "this-year":
      return { start: iso(new Date(y, 0, 1)), end: iso(new Date(y, 11, 31)) };
    case "last-year":
      return { start: iso(new Date(y - 1, 0, 1)), end: iso(new Date(y - 1, 11, 31)) };
    default:
      return { start: "", end: "" };
  }
};
export const SALES_PRESETS = [
  { id: "all", label: "All time" },
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "this-quarter", label: "This quarter" },
  { id: "last-quarter", label: "Last quarter" },
  { id: "this-year", label: "This year" },
  { id: "last-year", label: "Last year" },
  { id: "custom", label: "Custom range…" },
];

// Short two-tone chime for new orders (Web Audio — no asset needed).
export const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(1175, ctx.currentTime + 0.13);
    g.gain.setValueAtTime(0.09, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    o.start();
    o.stop(ctx.currentTime + 0.42);
  } catch (e) { /* audio not permitted */ }
};

export const urlB64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

export const monthRevenue = (orders) => {
  const now = new Date();
  return orders.filter((o) => o.status !== "cancelled").reduce((s, o) => {
    const d = new Date(o.created_at);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() ? s + (o.total || 0) : s;
  }, 0);
};

export const todayRevenue = (orders) => {
  const t = new Date().toDateString();
  return orders.filter((o) => o.status !== "cancelled" && new Date(o.created_at).toDateString() === t)
    .reduce((s, o) => s + (o.total || 0), 0);
};

export const promoLabel = (p) => {
  const kind = p.kind || (p.amount_off ? "amount" : "percent");
  if (kind === "bundle") return `${p.bundle_qty} for $${p.bundle_price}`;
  if (kind === "amount") return `$${p.amount_off} off`;
  if (kind === "freeship") return "Free shipping";
  return `${p.percent || 0}% off`;
};
